/**
 * useAgentPatchHandler — Handles AGENT_DATA_CHANGED_EVENT from the AI agent.
 * Phase 3B: patches TanStack Query cache directly instead of mutating local state.
 *
 * Handles 5+ change types:
 * - field_value_updated: direct value patch via queryClient.setQueryData
 * - field_reload: fetch updated field from API, patch tree cache
 * - block_created: add new building blocks to tree cache
 * - block_deleted: remove blocks from tree cache
 * - Scenario/sensitivity CRUD changes
 */

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import apiService from '../../../services/ApiService';
import FieldsInitializer, { getEndOfPeriod, initTimeSerie } from '../../../components/ModelDataFields/Input/Initializer';
import { AGENT_DATA_CHANGED_EVENT } from '../../../hooks/useAgentChat';
import type { AgentDataChange } from '../../../hooks/useAgentChat';
import IModelData from '../../../types/ModelData';
import { mapModelData } from '../services/dataMappers';
import { SCENARIO_TREE_KEY, type EditorState, patchTreeCache } from '../../../shared/hooks/queries/useScenarioTree';
import { SECTION_DATA_KEY } from '../../../shared/hooks/queries/useSectionLoader';

interface UseAgentPatchHandlerParams {
    scenarioId: number;
    model: any;
    currentScenario: any;
    currentSensitivity: any;
    loadSensitivityFromApi: () => void;
    setModel: React.Dispatch<any>;
    setCurrentScenario: React.Dispatch<any>;
    setCurrentSensitivity: React.Dispatch<any>;
}

function patchFieldInTree(
    state: EditorState,
    modelDataId: string,
    fieldName: string,
    patcher: (field: any) => any
): EditorState {
    const item = state.Input.tree.items[modelDataId];
    if (!item?.fields?.[fieldName]) return state;

    const updatedField = patcher({ ...item.fields[fieldName] });

    return {
        ...state,
        Input: {
            ...state.Input,
            tree: {
                ...state.Input.tree,
                items: {
                    ...state.Input.tree.items,
                    [modelDataId]: {
                        ...item,
                        fields: { ...item.fields, [fieldName]: updatedField },
                    },
                },
            },
        },
    };
}

export function useAgentPatchHandler({
    scenarioId,
    model,
    currentScenario,
    currentSensitivity,
    loadSensitivityFromApi,
    setModel,
    setCurrentScenario,
    setCurrentSensitivity,
}: UseAgentPatchHandlerParams) {
    const queryClient = useQueryClient();

    useEffect(() => {
        const patchTree = (updater: (prev: EditorState) => EditorState) =>
            patchTreeCache(queryClient, scenarioId, updater);

        const handler = (e: Event) => {
            const change = (e as CustomEvent<AgentDataChange>).detail;

            if (change.scenario_id && change.scenario_id !== currentScenario?.id) return;

            switch (change.type) {
                case 'field_value_updated': {
                    const modelDataId = String(change.model_data_id);
                    const fieldName = change.field_name as string;

                    patchTree((prev) => {
                        const item = prev.Input.tree.items[modelDataId];
                        if (!item?.fields?.[fieldName]) return prev;

                        return patchFieldInTree(prev, modelDataId, fieldName, (field) => {
                            if (change.merge_data && typeof change.merge_data === 'object') {
                                const dataArr = field.currentValue?.value?.data ?? field.value?.value?.data;
                                if (Array.isArray(dataArr)) {
                                    const periodicity = field.currentValue?.value?.timing?.periodicity?.value
                                        || field.value?.value?.timing?.periodicity?.value
                                        || 'monthly';
                                    const newData = [...dataArr];
                                    const mergeEntries = change.merge_data as Record<string, unknown>;
                                    for (const [date, val] of Object.entries(mergeEntries)) {
                                        const existingIdx = newData.findIndex((e: any) => e.date?.value === date);
                                        if (existingIdx >= 0) {
                                            newData[existingIdx] = {
                                                ...newData[existingIdx],
                                                value: { ...newData[existingIdx].value, value: val },
                                            };
                                        } else {
                                            newData.push({
                                                date: { value: date, address: null },
                                                end_of_period: { value: getEndOfPeriod(date, periodicity), address: null },
                                                value: { value: val, address: null },
                                            });
                                        }
                                    }

                                    if (field.currentValue?.value?.data) {
                                        return {
                                            ...field,
                                            currentValue: {
                                                ...field.currentValue,
                                                value: { ...field.currentValue.value, data: newData },
                                            },
                                        };
                                    }
                                    return {
                                        ...field,
                                        value: { ...field.value, value: { ...field.value.value, data: newData } },
                                    };
                                } else if (dataArr && typeof dataArr === 'object') {
                                    const merged = { ...dataArr, ...change.merge_data };
                                    if (field.currentValue?.value?.data) {
                                        return {
                                            ...field,
                                            currentValue: {
                                                ...field.currentValue,
                                                value: { ...field.currentValue.value, data: merged },
                                            },
                                        };
                                    }
                                    return field;
                                }
                                return field;
                            } else {
                                if (field.currentValue) {
                                    return { ...field, currentValue: { ...field.currentValue, value: change.new_value } };
                                } else if (field.value) {
                                    return { ...field, value: { ...field.value, value: change.new_value } };
                                }
                                return field;
                            }
                        });
                    });
                    break;
                }

                case 'field_reload': {
                    const fieldValueId = change.field_value_id as number;
                    const modelDataId = String(change.model_data_id);
                    const fieldName = change.field_name as string;

                    const patchField = async (itemId: string, fName: string, fId: number) => {
                        const resp = await apiService.getFieldValueCurrent(fId);
                        if (!resp?.ok) return;
                        const fieldData = await resp.json();

                        patchTree((prev) => {
                            const treeItem = prev.Input.tree.items[itemId];
                            if (!treeItem?.fields?.[fName]) return prev;

                            const existing = { ...treeItem.fields[fName] };
                            const tempField = { ...existing, value: fieldData.value };
                            FieldsInitializer({ [fName]: tempField });
                            const updated = {
                                ...existing,
                                id: fieldData.id,
                                model_data_id: fieldData.model_data_id,
                                name: fieldData.name,
                                kind: fieldData.kind,
                                compute_error: fieldData.compute_error,
                                value: tempField.value,
                                currentValue: tempField.currentValue,
                            };
                            if (tempField.start_dateStore) updated.start_dateStore = tempField.start_dateStore;
                            if (tempField.end_dateStore) updated.end_dateStore = tempField.end_dateStore;
                            if (tempField.store) updated.store = tempField.store;
                            if ('timingChoice' in (tempField.currentValue || {})) {
                                updated.currentValue = { ...updated.currentValue, timingChoice: tempField.currentValue.timingChoice };
                            }

                            return {
                                ...prev,
                                Input: {
                                    ...prev.Input,
                                    tree: {
                                        ...prev.Input.tree,
                                        items: {
                                            ...prev.Input.tree.items,
                                            [itemId]: {
                                                ...treeItem,
                                                fields: { ...treeItem.fields, [fName]: updated },
                                            },
                                        },
                                    },
                                },
                            };
                        });

                        window.dispatchEvent(new CustomEvent('agent:field_patched', {
                            detail: { fieldValueId: fId },
                        }));
                    };

                    const patchTimeSerie = async (itemId: string, fName: string, fId: number) => {
                        const resp = await apiService.getTimeSerie(fId);
                        if (!resp?.ok) return;
                        const newTs = await resp.json();

                        patchTree((prev) => {
                            const treeItem = prev.Input.tree.items[itemId];
                            if (!treeItem?.fields?.[fName]) return prev;

                            const existing = { ...treeItem.fields[fName] };
                            const updatedCurrentValue = {
                                ...existing.currentValue,
                                value: {
                                    ...existing.currentValue.value,
                                    data: newTs.data,
                                    timing: newTs.timing,
                                },
                                timingChoice: 'null',
                            };
                            const updatedField = { ...existing, currentValue: updatedCurrentValue };
                            initTimeSerie(updatedField);

                            return {
                                ...prev,
                                Input: {
                                    ...prev.Input,
                                    tree: {
                                        ...prev.Input.tree,
                                        items: {
                                            ...prev.Input.tree.items,
                                            [itemId]: {
                                                ...treeItem,
                                                fields: { ...treeItem.fields, [fName]: updatedField },
                                            },
                                        },
                                    },
                                },
                            };
                        });

                        window.dispatchEvent(new CustomEvent('agent:field_patched', {
                            detail: { fieldValueId: fId },
                        }));
                    };

                    const currentState = queryClient.getQueryData<EditorState>([SCENARIO_TREE_KEY, scenarioId]);
                    if (!currentState) break;

                    const targetItem = currentState.Input.tree.items[modelDataId];
                    const targetField = targetItem?.fields?.[fieldName];
                    const fetches: Promise<void>[] = [];

                    if (targetField) {
                        if (targetField.type === 'time_serie') {
                            fetches.push(patchTimeSerie(modelDataId, fieldName, fieldValueId));
                        } else {
                            fetches.push(patchField(modelDataId, fieldName, fieldValueId));
                        }

                        if (targetField.type === 'time_serie_timing') {
                            for (const [itemId, treeItem] of Object.entries(currentState.Input.tree.items) as [string, any][]) {
                                if (!treeItem?.fields) continue;
                                for (const [fName, f] of Object.entries(treeItem.fields) as [string, any][]) {
                                    if (f.type === 'time_serie' && f.id && f.currentValue?.value?.timing_id === targetField.id) {
                                        fetches.push(patchTimeSerie(itemId, fName, f.id));
                                    }
                                }
                            }
                        }
                    } else {
                        for (const [itemId, treeItem] of Object.entries(currentState.Input.tree.items) as [string, any][]) {
                            if (!treeItem?.fields) continue;
                            for (const [fName, f] of Object.entries(treeItem.fields) as [string, any][]) {
                                if (f.type === 'time_serie' && f.id && f.currentValue?.value?.timing_id === fieldValueId) {
                                    fetches.push(patchTimeSerie(itemId, fName, f.id));
                                } else if ((f.type === 'time_serie_timing' || f.type === 'customizable_timing_reference') && f.id === fieldValueId) {
                                    fetches.push(patchField(itemId, fName, f.id));
                                }
                            }
                        }
                    }

                    Promise.all(fetches).catch(() => {});
                    break;
                }

                case 'block_created': {
                    const blocks = change.blocks as Record<string, any>;
                    if (!blocks) break;

                    const parentId = String(change.parent_id);

                    patchTree((prev) => {
                        const newItems = { ...prev.Input.tree.items };

                        for (const [, rawBlock] of Object.entries(blocks)) {
                            const mapped = mapModelData(rawBlock as IModelData);
                            newItems[mapped.id] = mapped;
                        }

                        const parentItem = newItems[parentId];
                        if (parentItem) {
                            const newChildrens = [...parentItem.childrens];
                            for (const [, rawBlock] of Object.entries(blocks)) {
                                const blockParent = rawBlock.parent_id ? String(rawBlock.parent_id) : null;
                                const blockId = String(rawBlock.id);
                                if (blockParent === parentId && !newChildrens.includes(blockId)) {
                                    newChildrens.push(blockId);
                                }
                            }
                            newItems[parentId] = { ...parentItem, childrens: newChildrens };
                        }

                        return {
                            ...prev,
                            Input: {
                                ...prev.Input,
                                tree: { ...prev.Input.tree, items: newItems },
                            },
                        };
                    });
                    break;
                }

                case 'block_deleted': {
                    const blockId = String(change.building_block_id);
                    const parentId = String(change.parent_id);

                    patchTree((prev) => {
                        const newItems = { ...prev.Input.tree.items };

                        const toRemove = new Set<string>();
                        const collectDescendants = (id: string) => {
                            toRemove.add(id);
                            const item = newItems[id];
                            if (item?.childrens) {
                                for (const childId of item.childrens) {
                                    collectDescendants(childId);
                                }
                            }
                        };
                        collectDescendants(blockId);

                        for (const id of toRemove) {
                            delete newItems[id];
                        }

                        const parentItem = newItems[parentId];
                        if (parentItem?.childrens) {
                            newItems[parentId] = {
                                ...parentItem,
                                childrens: parentItem.childrens.filter(
                                    (id: string) => !toRemove.has(id)
                                ),
                            };
                        }

                        return {
                            ...prev,
                            Input: {
                                ...prev.Input,
                                tree: { ...prev.Input.tree, items: newItems },
                            },
                        };
                    });
                    break;
                }

                case 'block_renamed': {
                    const blockId = String(change.building_block_id);
                    const newName = change.new_name as string;

                    patchTree((prev) => {
                        const item = prev.Input.tree.items[blockId];
                        if (!item) return prev;

                        return {
                            ...prev,
                            Input: {
                                ...prev.Input,
                                tree: {
                                    ...prev.Input.tree,
                                    items: {
                                        ...prev.Input.tree.items,
                                        [blockId]: { ...item, name: newName },
                                    },
                                },
                            },
                        };
                    });
                    break;
                }

                case 'sensitivity_field_updated':
                case 'sensitivity_field_reload': {
                    loadSensitivityFromApi();
                    break;
                }

                case 'scenario_created': {
                    const scenario = change.scenario as any;
                    if (scenario && change.model_id === model.id) {
                        setModel((prev: any) => {
                            if (!prev) return prev;
                            const scenarios = prev.modelScenarios || prev.model_scenarios || [];
                            if (scenarios.some((s: any) => s.id === scenario.id)) return prev;
                            return { ...prev, modelScenarios: [...scenarios, scenario], model_scenarios: [...scenarios, scenario] };
                        });
                        setCurrentScenario(scenario);
                        setCurrentSensitivity('null');
                    }
                    break;
                }

                case 'scenario_deleted': {
                    if (change.model_id === model.id) {
                        setModel((prev: any) => {
                            if (!prev) return prev;
                            const scenarios = prev.modelScenarios || prev.model_scenarios || [];
                            const filtered = scenarios.filter((s: any) => s.id !== change.scenario_id);
                            return { ...prev, modelScenarios: filtered, model_scenarios: filtered };
                        });
                        if (currentScenario?.id === change.scenario_id) {
                            const scenarios = model.modelScenarios || model.model_scenarios || [];
                            const remaining = scenarios.filter((s: any) => s.id !== change.scenario_id);
                            if (remaining.length) {
                                setCurrentScenario(remaining[0]);
                            }
                        }
                    }
                    break;
                }

                case 'scenario_renamed': {
                    if (change.model_id === model.id) {
                        setModel((prev: any) => {
                            if (!prev) return prev;
                            const scenarios = prev.modelScenarios || prev.model_scenarios || [];
                            const updated = scenarios.map((s: any) =>
                                s.id === change.scenario_id ? { ...s, name: change.new_name } : s
                            );
                            return { ...prev, modelScenarios: updated, model_scenarios: updated };
                        });
                        if (currentScenario?.id === change.scenario_id) {
                            setCurrentScenario((prev: any) => ({ ...prev, name: change.new_name }));
                        }
                    }
                    break;
                }

                case 'sensitivity_created': {
                    const sens = change.sensitivity as any;
                    if (sens) {
                        setCurrentScenario((prev: any) => {
                            if (!prev || prev.id !== change.scenario_id) return prev;
                            const exists = prev.sensitivities?.some((s: any) => s.id === sens.id);
                            if (exists) return prev;
                            return { ...prev, sensitivities: [...(prev.sensitivities || []), sens] };
                        });
                        setCurrentSensitivity(sens);
                    }
                    break;
                }

                case 'sensitivity_deleted': {
                    setCurrentScenario((prev: any) => {
                        if (!prev || prev.id !== change.scenario_id) return prev;
                        return {
                            ...prev,
                            sensitivities: (prev.sensitivities || []).filter(
                                (s: any) => s.id !== change.sensitivity_id
                            ),
                        };
                    });
                    if (currentSensitivity !== 'null' && currentSensitivity?.id === change.sensitivity_id) {
                        setCurrentSensitivity('null');
                    }
                    break;
                }

                case 'sensitivity_renamed': {
                    setCurrentScenario((prev: any) => {
                        if (!prev || prev.id !== change.scenario_id) return prev;
                        return {
                            ...prev,
                            sensitivities: (prev.sensitivities || []).map((s: any) =>
                                s.id === change.sensitivity_id ? { ...s, name: change.new_name } : s
                            ),
                        };
                    });
                    if (currentSensitivity !== 'null' && currentSensitivity?.id === change.sensitivity_id) {
                        setCurrentSensitivity((prev: any) => ({ ...prev, name: change.new_name }));
                    }
                    break;
                }
            }
        };

        window.addEventListener(AGENT_DATA_CHANGED_EVENT, handler);
        return () => window.removeEventListener(AGENT_DATA_CHANGED_EVENT, handler);
    }, [queryClient, scenarioId, currentScenario?.id, currentSensitivity, model.id, loadSensitivityFromApi, setModel, setCurrentScenario, setCurrentSensitivity]);
}
